import * as React from "react";
import { Button, cn, FilterBlock, useTableExport, type SortingState } from "@racoon/ui";
import {
  useRoutesUrlState,
  getPillRouteStatuses,
  isDateBoundedRouteStatus,
} from "../model/useRoutesUrlState";
import { useRouteOptions } from "../model/useRouteOptions";
import { ROUTE_SORT_FIELDS } from "../model/route-sort";
import { useDispatchers } from "../model/useDispatchers";
import { getDispatcherInitials } from "../utils/getDispatcherInitials";
import {
  RoutesTable,
  getRoutesExportColumns,
  sortRoutesByPlanStartOrCreated,
} from "../components/RoutesTable";
import { usePseudoRouteModal } from "~/features/create-pseudo-route";
import { useRouteJobsModal } from "../hooks/useRouteJobsModal";
import { MswRouteListPage, MswTransferContext } from "../components/MswRouteListPage";
import { TtnRouteListPage } from "../components/TtnRouteListPage";
import { useSearchParams } from "react-router-dom";
import { useListTrucksQuery } from "~/shared/api/truck/truck.grpc";
import { TRUCK_TYPE_ORDER, isTruckArchived, truckTypeOf } from "~/shared/lib/truck-type";
import { humanTruckType } from "../../autopark/helpers";
import { IcUpArrowBlue } from "@racoon/icons";
import { Namespace, useT } from "@racoon/i18n";
import { useCallback, useMemo } from "react";
import { ConnectProto } from "@racoon/api";
import { useMediaQuery } from "~/shared/hooks/useMediaQuery";
import {
  routeGrpc,
  useLazyListRoutesQuery,
  useListRoutesQuery,
} from "~/shared/api/routes/route.grpc";
import { toTimestamp, getEnumName, getEnumValue } from "@racoon/lib";
import { useSelectedCompany } from "~/shared/store/company/hooks/useSelectedCompany";
import { useAccumulatedList } from "~/shared/hooks/use-accumulated-list";
import { toListSort } from "~/shared/lib/list-sort";

const ROUTES_PAGE_SIZE = 100;

const selectRouteRows = (data: ConnectProto.RouteList) => data.routes;
const selectRouteKey = (route: ConnectProto.Route) => String(route.id);
const selectListTotal = (data: ConnectProto.RouteList) => data.listInfo?.totalItems;
const jsonWithBigint = (_key: string, value: unknown) =>
  typeof value === "bigint" ? String(value) : value;

export const RoutesPage: React.FC = () => {
  const { openPseudoRoute } = usePseudoRouteModal();
  const [selectedRoute, setSelectedRoute] = React.useState<ConnectProto.Route | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightShiftId = searchParams.get("highlightShift");
  const isMd = useMediaQuery("(min-width: 768px)");

  const { selectedCompanyId } = useSelectedCompany();

  const statusFromUrl = searchParams.get("status") ?? "all";
  const startFromUrl = searchParams.get("start");
  const endFromUrl = searchParams.get("end");
  const truckTypeFromUrl = searchParams.get("truck_type");
  const truckType = truckTypeFromUrl
    ? getEnumValue(ConnectProto.TruckTypeSchema, truckTypeFromUrl)
    : undefined;
  const routeStatusFromUrl = searchParams.get("routeStatus") ?? "all";

  // The registry's select filters and search, read off the URL for the request — the
  // server applies them now, so a page and its total always describe the same set.
  const driverIdFromUrl = searchParams.get("driverId");
  const customerIdFromUrl = searchParams.get("customerId");
  const objectIdFromUrl = searchParams.get("objectId");
  const jobTypeFromUrl = searchParams.get("jobType");
  const searchFromUrl = searchParams.get("search") ?? "";
  const todayStart = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }, []);

  const todayEnd = useMemo(() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d.toISOString();
  }, []);

  // The status pill from the URL as a numeric enum value ("all" when unset) — the
  // URL stores the readable enum name.
  const routeStatusValue = useMemo(() => {
    if (routeStatusFromUrl === "all") return "all";
    const value =
      getEnumValue(ConnectProto.RouteStatusSchema, routeStatusFromUrl) ??
      (Number.isNaN(Number(routeStatusFromUrl)) ? undefined : Number(routeStatusFromUrl));
    return value === undefined ? "all" : String(value);
  }, [routeStatusFromUrl]);

  // In-flight statuses ("Назначено", "Запланировано", "В процессе", "Возвращается")
  // are a bounded set: query them by status alone and show every route, no window.
  const isDateBounded = isDateBoundedRouteStatus(routeStatusValue);

  const listRoutesParams = useMemo(() => {
    const params: Partial<ConnectProto.RouteListRequest> = {
      ownerOrganizationIds: selectedCompanyId ? [BigInt(selectedCompanyId)] : [],
    };

    if (isDateBounded && statusFromUrl !== "assigned" && statusFromUrl !== "active") {
      const startDate = new Date(startFromUrl ?? todayStart);
      const endDate = new Date(endFromUrl ?? todayEnd);

      params.datetimeFilter = {
        from: toTimestamp(startDate),
        to: toTimestamp(endDate),
      } as ConnectProto.RouteListRequest["datetimeFilter"];
    }

    // A pill can stand for several statuses (e.g. "Выполнено" covers
    // FORCE_COMPLETED too), so ask for the whole group.
    const pillStatuses = getPillRouteStatuses(routeStatusValue);
    if (pillStatuses.length > 0) {
      params.routeStatuses = pillStatuses;
    }

    if (driverIdFromUrl && /^\d+$/.test(driverIdFromUrl)) params.driverId = BigInt(driverIdFromUrl);
    if (jobTypeFromUrl) {
      const jobTypeNumber = getEnumValue(ConnectProto.JobTypeSchema, jobTypeFromUrl);
      if (jobTypeNumber !== undefined) params.jobTypes = [jobTypeNumber];
    }
    if (searchFromUrl.trim()) params.search = searchFromUrl;

    return params;
  }, [
    selectedCompanyId,
    statusFromUrl,
    startFromUrl,
    endFromUrl,
    routeStatusValue,
    isDateBounded,
    todayStart,
    todayEnd,
    driverIdFromUrl,
    jobTypeFromUrl,
    searchFromUrl,
  ]);

  // Customer and object are properties of the route's jobs, the truck type one of its
  // shift's truck — the request cannot express them. While any is chosen the query runs
  // unpaginated (never worse than the pre-pagination behaviour, and the match — inside
  // the date window — is small) and the rows are narrowed client-side below.
  const isClientFiltered = Boolean(customerIdFromUrl || objectIdFromUrl || truckType !== undefined);

  // The table's column sort. Paged, the server sorts the whole match and the table shows
  // it as it comes; in the unpaginated mode the table sorts the rows itself.
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const pagedParams = useMemo(() => {
    // The unpaginated mode sorts in the table: a sort there must not refetch the match.
    if (isClientFiltered) return listRoutesParams;
    const sort = toListSort(sorting, ROUTE_SORT_FIELDS);
    return sort ? { ...listRoutesParams, listFilter: sort } : listRoutesParams;
  }, [listRoutesParams, sorting, isClientFiltered]);

  // Serialized request, the server's sort included: any change resets the accumulator
  // back to page 0.
  const filterKey = useMemo(() => JSON.stringify(pagedParams, jsonWithBigint), [pagedParams]);

  const accumulated = useAccumulatedList<ConnectProto.RouteList, ConnectProto.Route>({
    useListQuery: useListRoutesQuery,
    listEndpoint: routeGrpc.endpoints.listRoutes,
    arg: pagedParams,
    pageSize: ROUTES_PAGE_SIZE,
    filterKey,
    selectRows: selectRouteRows,
    selectTotal: selectListTotal,
    selectRowKey: selectRouteKey,
    // The sheet and TTN views load their own shift-scoped routes.
    skip: statusFromUrl === "sheet" || statusFromUrl === "ttn" || isClientFiltered,
  });

  // The customer/object mode fetches the whole match in one unpaginated query.
  const { data: unpagedResponse, isFetching: isFetchingUnpaged } = useListRoutesQuery(
    listRoutesParams as Parameters<typeof useListRoutesQuery>[0],
    { skip: statusFromUrl === "sheet" || statusFromUrl === "ttn" || !isClientFiltered },
  );

  // The registry shows one page but the export must cover the whole match, so it
  // re-runs the same query without list_filter at click time.
  const [triggerListRoutes] = useLazyListRoutesQuery();
  const fetchAllForExport = useCallback(async () => {
    const response = await triggerListRoutes(
      listRoutesParams as Parameters<typeof triggerListRoutes>[0],
    ).unwrap();
    return sortRoutesByPlanStartOrCreated(response.routes ?? []);
  }, [triggerListRoutes, listRoutesParams]);

  const data = useMemo(() => {
    return isClientFiltered ? (unpagedResponse?.routes ?? []) : accumulated.rows;
  }, [isClientFiltered, unpagedResponse, accumulated.rows]);
  const isFetching = isClientFiltered ? isFetchingUnpaged : accumulated.isFetching;

  // The customer/object narrowing for the unpaginated mode: a route matches when any of
  // its jobs belongs to the chosen customer / pickup location — the same relation the
  // jobs carry on the wire.
  const rows = useMemo(() => {
    if (!isClientFiltered) return data;
    return data.filter((route) => {
      const jobs = route.jobs ?? [];
      if (
        customerIdFromUrl &&
        !jobs.some(
          (j) => String(j.serviceReceiver?.id ?? j.serviceReceiverId) === customerIdFromUrl,
        )
      ) {
        return false;
      }
      if (truckType !== undefined) {
        const truck = route.driverOnShift?.truck;
        if (!truck || truckTypeOf(truck) !== truckType) return false;
      }
      if (
        objectIdFromUrl &&
        !jobs.some((j) => String(j.pickupLocation?.id ?? j.pickupLocationId) === objectIdFromUrl)
      ) {
        return false;
      }
      return true;
    });
  }, [data, isClientFiltered, customerIdFromUrl, objectIdFromUrl, truckType]);

  // Routes currently shown in the MSW sheet view (selected vehicle/shift). The
  // "Утвердить маршрутный лист" button approves these, not the page-level `data`
  // query (which uses a different date/filter and isn't scoped to the shift).
  const [, setMswContext] = React.useState<MswTransferContext | null>(null);

  const t = useT();

  // Get shift data for route highlighting
  const { data: shiftRoutesResponse } = useListRoutesQuery(
    {
      ownerOrganizationIds: selectedCompanyId ? [BigInt(selectedCompanyId)] : [],
      driverOnShiftIds:
        highlightShiftId && /^\d+$/.test(highlightShiftId) ? [BigInt(highlightShiftId)] : [],
    },
    { skip: !highlightShiftId || !/^\d+$/.test(highlightShiftId) },
  );

  const shiftData = useMemo(() => {
    return shiftRoutesResponse?.routes || [];
  }, [shiftRoutesResponse]);

  // Find shift by ID
  const targetRoute = shiftData?.find(
    (route: ConnectProto.Route) => route.driverOnShift?.id?.toString() === highlightShiftId,
  );

  const { drivers, objects, customers } = useRouteOptions(data);
  const { dispatchersMap, isLoading: dispatchersLoading } = useDispatchers();

  const {
    states,
    selectedState,
    routeStatusPills,
    selectedRouteStatus,
    onRouteStatusChange,
    startDate,
    endDate,
    onStateChange,
    onFilterParamsChange,
    onDateChange,
    onSearchChange,
    searchValue,
    filterParams,
  } = useRoutesUrlState({
    frontFilterOnly: true,
    options: { drivers, objects, customers },
  });

  const handleFilterParamsChange = (key: string, value: string | null) => {
    onFilterParamsChange(key, value || "");
  };

  const { openRouteJobsWithNavigation } = useRouteJobsModal();

  const handleRouteSelect = (route: ConnectProto.Route) => {
    setSelectedRoute(route);
    openRouteJobsWithNavigation(rows, route, {
      onActiveItemChange: (activeRoute) => {
        setSelectedRoute(activeRoute);
      },
      onClose: () => {
        setSelectedRoute(null);
      },
    });
  };

  const isSheetState = selectedState?.value === "sheet";
  const isTtnState = selectedState?.value === "ttn";

  // Every view is filtered by truck type rather than route category: the types of
  // the organization's non-archived trucks, in the fleet's own order.
  const { data: fleet } = useListTrucksQuery(
    { ownerOrganizationIds: selectedCompanyId ? [BigInt(selectedCompanyId)] : [] },
    { skip: !selectedCompanyId },
  );
  const truckTypeCategories = useMemo(() => {
    const active = new Set(
      (fleet?.trucks ?? []).filter((truck) => !isTruckArchived(truck)).map(truckTypeOf),
    );
    return [
      { key: t(Namespace.Common, "all") ?? "Все", value: "all" },
      ...TRUCK_TYPE_ORDER.filter(
        (type) => type !== ConnectProto.TruckType.UNDEFINED && active.has(type),
      ).map((type) => ({
        key: humanTruckType(type, t),
        value: getEnumName(ConnectProto.TruckTypeSchema, type) ?? String(type),
      })),
    ];
  }, [fleet, t]);
  const onTruckTypeChange = (value: string) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === "all") next.delete("truck_type");
      else next.set("truck_type", value);
      return next;
    });

  // The sheet and TTN views own their own date/vehicle pickers, so the page-level
  // date range, search and select filters don't apply to them.
  const hidePageFilters = isSheetState || isTtnState;
  const showDatePicker = !hidePageFilters && isDateBounded;

  // The route sheet ("Маршрутный лист") and the TTN view are their own views with their
  // own controls — the status pills belong to the registry only, same as the date
  // picker and search.
  const statusPills = useMemo(
    () =>
      hidePageFilters ? [] : routeStatusPills.map((p) => ({ key: p.key, value: String(p.value) })),
    [hidePageFilters, routeStatusPills],
  );

  const getDispatcherInitialsForExport = useCallback(
    (authorId: number | undefined): string => {
      return getDispatcherInitials(authorId, dispatchersMap);
    },
    [dispatchersMap],
  );

  const sortedDataForExport = useMemo(() => {
    return sortRoutesByPlanStartOrCreated(rows || []);
  }, [rows]);

  const { handleExportExcel } = useTableExport({
    data: sortedDataForExport,
    // In the client-filtered mode the full match is already loaded (and narrowed) in
    // sortedDataForExport; only the paginated mode needs the click-time full fetch.
    ...(isClientFiltered ? {} : { getData: fetchAllForExport }),
    columns: getRoutesExportColumns(t, getDispatcherInitialsForExport),
    reportName: t(Namespace.Routes, "routes"),
    // No window applied, no window in the filename.
    startDate: showDatePicker ? startDate : undefined,
    endDate: showDatePicker ? endDate : undefined,
  });

  return (
    <div
      className={cn(
        "w-full min-w-0 flex flex-col min-h-0 flex-1",
        isSheetState ? "gap-6" : "gap-4",
      )}
    >
      <FilterBlock
        states={states}
        direction="column"
        selectedState={selectedState}
        onStateChange={onStateChange}
        categories={truckTypeCategories}
        selectedCategory={(truckType !== undefined && truckTypeFromUrl) || "all"}
        onCategoryChange={onTruckTypeChange}
        pills={statusPills}
        selectedPill={selectedRouteStatus}
        onPillChange={onRouteStatusChange}
        filterParams={hidePageFilters ? [] : filterParams}
        startDate={showDatePicker ? startDate : undefined}
        endDate={showDatePicker ? endDate : undefined}
        onFilterParamsChange={handleFilterParamsChange}
        statesWithCategories
        statesVariant="tab"
        onDateChange={showDatePicker ? onDateChange : undefined}
        onSearchChange={hidePageFilters ? undefined : onSearchChange}
        searchValue={hidePageFilters ? undefined : searchValue}
        itemType="routes"
        additionalButtonsPosition="bottom"
        // Passing nothing (instead of buttons that render empty) keeps the whole
        // controls row out of the layout in the sheet state. The TTN view loads its
        // own routes, so the registry's pseudo-route and export do not apply there.
        additionalButtons={
          isSheetState || isTtnState ? undefined : (
            <>
              <span className="hidden md:block">
                <Button
                  id="routes-pseudo-button"
                  variant="opacityBlueWithoutOutline"
                  className="gap-1"
                  size={"sm"}
                  onClick={() => openPseudoRoute()}
                >
                  <IcUpArrowBlue className="text-brand-blue w-4 h-4" />
                  {t(Namespace.Routes, "pseudoRoute")}
                </Button>
              </span>
              <span className="hidden md:block">
                <Button
                  id="routes-export-button"
                  variant="excel"
                  size={"sm"}
                  onClick={handleExportExcel}
                ></Button>
              </span>
            </>
          )
        }
        additionalButtonsVisible={isMd}
        inlineCategories={false}
      />

      {isSheetState ? (
        <MswRouteListPage onTransferContextChange={setMswContext} />
      ) : isTtnState ? (
        <TtnRouteListPage />
      ) : (
        <>
          <RoutesTable
            data={rows}
            loading={(isFetching && rows.length === 0) || dispatchersLoading}
            highlightShiftId={highlightShiftId}
            targetRoute={targetRoute}
            selectedRouteId={selectedRoute?.id}
            onRouteSelect={handleRouteSelect}
            dispatchersMap={dispatchersMap}
            manualSorting={!isClientFiltered}
            sorting={sorting}
            onSortChange={setSorting}
            onEndReached={
              !isClientFiltered && accumulated.hasMore ? accumulated.loadNextPage : undefined
            }
            loadingMore={isFetching && data.length > 0}
          />
        </>
      )}

      {/* RouteJobsModal is handled by useRouteJobsModal hook above */}
    </div>
  );
};
